import LockIcon from "@mui/icons-material/Lock";
import { Alert } from "@mui/material";
import { formatNotReportingReason } from "encamp-shared/src/submissions";
import { SubmissionStatus } from "generated-graphql/graphql";
import { useWasteStepper } from "hooks/useWasteStepper";

/**
 * Read-only banner shown atop every frozen (post-snapshot) stepper view.
 *
 * There is intentionally no action here. "Mark as filing complete" — the normal
 * post-approval flip to `FILING_COMPLETE` while `PROCESSING_SUBMISSION` — lives
 * on the Filing Progress landing (staff-only). The old staff-only "Force
 * Complete" bypass (completing a not-yet-approved submission) has been
 * deprecated and removed; force-completing is no longer allowed.
 *
 * Reverting a filed submission to an earlier status is a staff operation in the
 * submissions-list Change Status action, not the stepper. `FILING_COMPLETE` /
 * `NOT_REPORTING` are terminal-ish and expose no actions.
 */
export function FrozenSubmissionBanner() {
  const { submission } = useWasteStepper();

  if (submission?.status === SubmissionStatus.ProcessingSubmission) {
    return (
      <Alert severity="info" icon={<LockIcon fontSize="inherit" />}>
        Submitted for processing. The details below are the frozen snapshot taken at the time of
        submission; the filing automation will complete it.
      </Alert>
    );
  }

  if (submission?.status === SubmissionStatus.NotReporting) {
    const reasonText = formatNotReportingReason(
      submission.notReportingReason,
      submission.notReportingReasonNote
    );
    return (
      <Alert severity="info" icon={<LockIcon fontSize="inherit" />}>
        {reasonText
          ? `This submission is not being reported (${reasonText}) and is read-only.`
          : "This submission is not being reported and is read-only."}
      </Alert>
    );
  }

  return (
    <Alert severity="info" icon={<LockIcon fontSize="inherit" />}>
      This submission has been filed and is read-only. The details below are a snapshot taken at the
      time of filing.
    </Alert>
  );
}
